import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {CSSTransition} from 'react-transition-group';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {SVGIcon} from '@homeaway/react-svg';
import {CLOSE__16} from '@homeaway/svg-defs';
import classnames from 'classnames';
/* eslint-disable no-multi-spaces */

class ModalStructure extends Component {
    static propTypes = {
        /** id attribute for the modal. Used to generate aria-labeledby attributes as well */
        id: PropTypes.string.isRequired,
        /** Flag to determine if the Modal has a close "x" button in the header */
        closeButton: PropTypes.bool,
        /** Screen Reader only text for close button in Modal Header */
        closeText: PropTypes.string.isRequired,
        /** Flag for if the Modal Header is shown */
        header: PropTypes.bool,
        /** The callback function for the secondary action button. Only applicable for isWorkflow modals at "mobile" widths. */
        secondaryActionCallback: PropTypes.func,
        /** Flag to determine if secondary action button is disabled. Only applicable for isWorkflow modals at "mobile" widths. */
        secondaryActionDisabled: PropTypes.bool,
        /** The text or html to display on the secondary action button. Only applicable for isWorkflow modals at "mobile" widths. */
        secondaryActionLabel: PropTypes.node,
        /** The Modal Heading in .modal-title. Used as the label for the modal if provided. */
        title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** React Component for Modal footer */
        footer: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
        /** Flag to determine if the Modal should display a loading overlay */
        isLoading: PropTypes.bool,
        /** Flag to determine if the Modal should be styled as "instructional" */
        isInstructional: PropTypes.bool,
        /** Flag to determine if the Modal should be styled as "modal-content--workflow" (more compact mobile styling) */
        isWorkflow: PropTypes.bool,
        /** Flag to determine if the Modal should be styled as "contextual" (mobile styling that is not full viewport size in order to help maintain context) */
        isContextual: PropTypes.bool,
        /** ReactCSSTransitionGroup transitionName for modal animation */
        modalAnimation: PropTypes.oneOf(['modal-from-center', 'modal-from-bottom', 'modal-from-left', 'modal-from-right', 'none']),
        /** Flag to determine if the dark backdrop should be rendered. **/
        renderBackdrop: PropTypes.bool,
        /** Timeout object for transitions */
        timeouts: PropTypes.shape({
            backdrop: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            }),
            modal: PropTypes.shape({
                transitionEnterTimeout: PropTypes.number,
                transitionExitTimeout: PropTypes.number
            })
        })
    }

    static defaultProps = {
        closeButton: null,
        header: null,
        footer: null,
        modalAnimation: 'modal-from-center',
        parentNode: null,
        portalClassName: null,
        renderBackdrop: true,
        title: null,
        timeouts: {
            backdrop: {
                transitionEnterTimeout: 250,
                transitionExitTimeout: 250
            },
            modal: {
                transitionEnterTimeout: 350,
                transitionExitTimeout: 350
            }
        }
    }

    /** The .modal-overlay class is contained WITHIN the Modal markup itself,
     *  thus we need to manually handle determining if the user is clicking outside
     *  the modal dialog.
     */
    handleModalClick = (e) => {
        // Do not do anything as we are specifying false for closeOnOutsideClick
        if (this.props.closeOnOutsideClick) {
            // currentTarget here is the `modal` div, which covers the entire screen
            // any clicks on the backdrop will have a target of modal
            // any clicks on the modal itself will have a target of modal-body, model-header, etc
            if (e.target !== e.currentTarget) {
                return;
            }
            e.stopPropagation();
            this.props.onClose();
        }
    }

    // Helps with iOS specific bug: swiping on header or footer steals some sort of
    // focus that should be on modal-body, thus causing natural scrolling to freeze
    // until modal-body is tapped again
    preventTouchMove(e) {
        e.preventDefault();
    }

    renderBackdrop = () => {
        return (
            <div tabIndex="-1" className="Modal__backdrop" />
        );
    }

    renderModalHeader() {
        const {
            closeButton,
            closeText,
            secondaryActionLabel,
            secondaryActionCallback,
            secondaryActionDisabled,
            onClose,
            title,
            isWorkflow,
            id
        } = this.props;

        const hasSecondaryAction = secondaryActionLabel && secondaryActionCallback && isWorkflow;
        const headerClass = classnames('Modal__header', {
            'Modal__header--no-close-btn': !closeButton,
            'Modal__header--no-title': !title
        });
        const titleClass = classnames('Modal__header-column', 'Modal__header-column--title ', {
            'Modal__header-column--center-align': hasSecondaryAction
        });

        return (
            <div onTouchMove={this.preventTouchMove} className={headerClass}>
                {hasSecondaryAction &&
                    <div className="Modal__header-column Modal__header-column--secondary-action">
                        <button
                            type="button"
                            onClick={secondaryActionCallback}
                            className="Modal__header-btn Modal__header-btn--secondary-action"
                            {...(secondaryActionDisabled && {disabled: true})}
                        >
                            <span className="Modal__button-flex Modal__button-flex--secondary-action">
                                {secondaryActionLabel}
                            </span>
                        </button>
                    </div>
                }
                {title &&
                    <div className={titleClass}>
                        <h4 id={`${id}-label`} className="Modal__title">{title}</h4>
                    </div>
                }
                {closeButton &&
                    <div className="Modal__header-column Modal__header-column--close">
                        <button type="button" onClick={onClose} className="Modal__header-btn Modal__header-btn--close" aria-label={closeText}>
                            <span className="Modal__button-flex" aria-hidden="true">
                                <SVGIcon markup={CLOSE__16}/>
                            </span>
                        </button>
                    </div>
                }
            </div>
        );
    }

    renderModalContent() {
        const {
            children,
            className,
            header,
            footer,
            id,
            isInstructional,
            isLoading,
            isWorkflow,
            isContextual,
            modalBodyRef,
            // grab unused vars to prevent rest from being passed down as propertys on the modal div
            closeOnEsc,             // eslint-disable-line no-unused-vars
            closeOnOutsideClick,    // eslint-disable-line no-unused-vars
            isOpen,                 // eslint-disable-line no-unused-vars
            modalAnimation,         // eslint-disable-line no-unused-vars
            timeouts,               // eslint-disable-line no-unused-vars
            title,                  // eslint-disable-line no-unused-vars
            onClose,                // eslint-disable-line no-unused-vars
            closeButton,            // eslint-disable-line no-unused-vars
            closeText               // eslint-disable-line no-unused-vars
        } = this.props;

        const modalStyles = {
            display: 'block'
        };        
        const modalClassName = classnames(className, 'Modal', {
            'Modal--contextual': isContextual
        });
        const modalContentClassName = classnames(
            'Modal__content',
            isInstructional ? 'Modal__content--instructional' : '',
            isWorkflow ? 'Modal__content--workflow' : ''
        );
        const modalBodyClassName = classnames('Modal__body', {
            'Modal__body--no-header': !header
        });

        return (
            <div
                ref={(c) => {
                    this.dialog = c;
                }}
                className={modalClassName}
                id={id}
                style={modalStyles}
                onClick={this.handleModalClick}
                tabIndex="-1"
                role="dialog"
                aria-labelledby={`${id}-label`}
                aria-modal="true"
            >
                <div className="Modal__dialog">
                    <div className={modalContentClassName} role="document">
                        {isLoading &&
                            <LoadingOverlay/>
                        }
                        {header &&
                            this.renderModalHeader()
                        }
                        <div ref={modalBodyRef} className={modalBodyClassName}>
                            {children}
                        </div>
                        {footer &&
                            <div onTouchMove={this.preventTouchMove} className="Modal__footer">
                                {footer}
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    renderModalWrapper() {
        const {
            // grab unused vars to prevent rest from being passed down as propertys on the modal div
            closeOnEsc,             // eslint-disable-line no-unused-vars
            closeOnOutsideClick,    // eslint-disable-line no-unused-vars
            isOpen,
            modalAnimation,
            renderBackdrop,
            portalClassName,
            timeouts
        } = this.props;

        const useModalAnimation = modalAnimation !== 'none';

        return (
            <div className={portalClassName}>
                {renderBackdrop && useModalAnimation &&
                    <CSSTransition
                        in={isOpen}
                        classNames="Modal__backdrop"
                        timeout={{
                            enter: timeouts.backdrop.transitionEnterTimeout,
                            exit: timeouts.backdrop.transitionExitTimeout
                        }}
                        mountOnEnter
                        unmountOnExit
                    >
                        {this.renderBackdrop()}
                    </CSSTransition>
                }
                {renderBackdrop && !useModalAnimation && isOpen && this.renderBackdrop()}
                {useModalAnimation &&
                    <CSSTransition
                        in={isOpen}
                        classNames={modalAnimation}
                        timeout={{
                            enter: timeouts.modal.transitionEnterTimeout,
                            exit: timeouts.modal.transitionExitTimeout
                        }}
                        mountOnEnter
                        unmountOnExit
                    >
                        {this.renderModalContent()}
                    </CSSTransition>
                }
                {!useModalAnimation && isOpen && this.renderModalContent()}
            </div>
        );
    }

    render() {
        const {parentNode} = this.props;
        const usePortal = parentNode && typeof window !== 'undefined';

        return (
            <>
                {!usePortal &&
                    this.renderModalWrapper()
                }
                {usePortal &&
                    ReactDOM.createPortal(this.renderModalWrapper(), parentNode)
                }
            </>
        );
    }
}

export default ModalStructure;
